import React, { useState } from 'react';
import { Route, RouteComponentProps } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Checkbox,
  CircularProgress,
  createStyles,
  FormControlLabel,
  Grid,
  makeStyles,
  Theme,
  Toolbar,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';

import { primaryGreen } from 'src/data/colors';
import { Lifecycle, SimpleRequestStatus } from '../../../state/request';
import { PathPattern, AlarmDetailsRouteMatch } from '../../../routing';
import { RequestStatus } from '../../utility/RequestStatus';
import { AlarmsPageQuery } from '../../../state/contexts/alarmsPage';
import { AlarmSummary, Site } from '../../../models';

import { AlarmsTableContainer } from './AlarmsTableContainer';
import { AlarmQueryModal } from './AlarmQueryModal';
import { AlarmDetailsLoader } from './AlarmDetailsLoader';
import { AlarmsQueryInfobar } from './AlarmsQueryInfobar';
import { PrimaryButton, SecondaryButton } from 'src/components/ui/Buttons';
import Subheader from 'src/components/ui/subheader';

import { useAlarmDownloadCSV } from 'src/helpers/useDownloadCSV';

const PAGE_SIZE = 25;

export interface Props {
  site: Site;
  page: number;
  query?: AlarmsPageQuery;
  isActiveOnly: boolean;
  alarmSummaries: AlarmSummary[];
  requestStatus?: SimpleRequestStatus;
  setQuery: (query: AlarmsPageQuery | null) => void;
  setPage: (page: number) => void;
  setIsActiveOnly: (isActiveOnly: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actions: {
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      marginTop: 8,

      [theme.breakpoints.down('sm')]: {
        alignItems: 'flex-start',
        flexDirection: 'column',
      },
    },
    oneAction: {
      justifyContent: 'flex-end',
    },
    actionsRight: {
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
      },
    },
    outer: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    body: {
      position: 'relative',
      flex: 1,
      overflow: 'auto',
      width: '100%',
      padding: '0 16px',
    },
    statusWrapper: {
      top: '46px',
      left: '0',
      width: '100%',
      padding: 24,
      textAlign: 'center',
      position: 'absolute',
    },
    header: {
      padding: theme.spacing(2),
    },
    footer: {
      borderTop: '1px solid #E8E8E8',
    },
    checkbox: {
      color: primaryGreen,
      '&.Mui-checked': {
        color: primaryGreen,
      },
    },
    alarmsCount: {
      fontSize: 22,
      fontWeight: 500,
    },
    pagination: {
      '& .MuiPaginationItem-root': {
        backgroundColor: 'white',
      },

      '& .MuiPaginationItem-root.Mui-selected': {
        border: `1px solid ${primaryGreen}`,
        borderRadius: '8px',
      },
    },
  })
);

export const AlarmsPage: React.FC<Props> = (props) => {
  const {
    query,
    page,
    alarmSummaries,
    setPage,
    site,
    setQuery,
    requestStatus,
    isActiveOnly,
    setIsActiveOnly,
  } = props;
  const classes = useStyles();

  const isLoaded = requestStatus === Lifecycle.COMPLETED;
  const isPending = !requestStatus || requestStatus === Lifecycle.LOADING;
  const error = typeof requestStatus === 'object' ? requestStatus : undefined;

  const firstIndex = page ? (page - 1) * PAGE_SIZE : 0;
  const pageElements = alarmSummaries.slice(firstIndex, firstIndex + PAGE_SIZE);

  const [queryModalOpen, setQueryModalOpen] = useState<boolean>(false);
  const { downloading, download } = useAlarmDownloadCSV(alarmSummaries, site);

  return (
    <Grid data-testid="alarms-page" container className={classes.outer}>
      <Grid item container alignItems="baseline">
        <Subheader title="alarms">
          <div
            style={{
              display: 'flex',
            }}
          >
            <PrimaryButton onClick={() => setQueryModalOpen(true)}>
              {!query ? 'Create Query' : 'Edit Query'}
            </PrimaryButton>
            {query && (
              <Grid item style={{ marginLeft: 16 }}>
                <SecondaryButton onClick={() => setQuery(null)}>Clear Query</SecondaryButton>
              </Grid>
            )}
          </div>
        </Subheader>
      </Grid>
      <Grid item container className={classes.header}>
        <Grid
          className={`${classes.actions} ${!pageElements.length && classes.oneAction}`}
          container
          item
        >
          {alarmSummaries.length > 0 && isLoaded && (
            <Grid item data-testid="alarms-page-result-count" className={classes.alarmsCount}>
              {alarmSummaries.length} alarms
            </Grid>
          )}
          {!query && isLoaded && (
            <>
              <Grid item className={classes.actionsRight}>
                <FormControlLabel
                  style={{ marginRight: 8 }}
                  control={
                    <Checkbox
                      onChange={(e) => setIsActiveOnly(e.target.checked)}
                      checked={isActiveOnly}
                      name="activeOnly"
                      classes={{ root: classes.checkbox }}
                    />
                  }
                  label="Active Only"
                />
                <SecondaryButton
                  onClick={() => download()}
                  disabled={downloading || !pageElements.length}
                >
                  Download CSV {downloading && <CircularProgress size={16} style={{ margin: 8 }} />}
                </SecondaryButton>
              </Grid>
            </>
          )}
        </Grid>
      </Grid>

      <Grid item className={classes.body}>
        {query && <AlarmsQueryInfobar currentQuery={query} site={site} />}
        {/* Table */}
        {isLoaded && <AlarmsTableContainer siteId={site.id} pageElements={pageElements} />}

        {(isPending || error) && (
          <div className={classes.statusWrapper}>
            <RequestStatus status={requestStatus} />
          </div>
        )}
      </Grid>

      {/* Pagination */}
      {pageElements.length > 0 && isLoaded && (
        <Grid item className={classes.footer}>
          <Toolbar>
            <Grid justifyContent="center" container alignItems="center" spacing={2}>
              <Grid item>
                <Pagination
                  count={Math.ceil(alarmSummaries.length / PAGE_SIZE)}
                  page={page || 1}
                  variant="outlined"
                  shape="rounded"
                  onChange={(_e, newPage) => setPage(newPage)}
                  classes={{
                    ul: classes.pagination,
                  }}
                />
              </Grid>
            </Grid>
          </Toolbar>
        </Grid>
      )}

      {queryModalOpen && (
        <AlarmQueryModal
          site={site}
          currentQuery={query}
          onCancel={() => {
            setQueryModalOpen(false);
          }}
          onSubmitQuery={(newQuery) => {
            setQuery(newQuery);
            setQueryModalOpen(false);
          }}
        />
      )}

      <Route
        path={PathPattern.ALARM_DETAILS}
        // @ts-ignore todo: fix type
        render={(route: RouteComponentProps<AlarmDetailsRouteMatch>) => {
          const { alarmId, alarmTagId } = route.match.params;
          return <AlarmDetailsLoader alarmId={alarmId} alarmTagId={alarmTagId} />;
        }}
      />
    </Grid>
  );
};
